import type { VariantProps } from 'class-variance-authority';
import type { ReactNode, Ref, RefAttributes, RefObject } from 'react';

import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { useMemo } from 'react';

const colorVariants = {
  solid: {
    brand: 'bg-brand text-brand-foreground',
    orange: 'bg-orange text-orange-foreground',
    muted: 'bg-muted text-muted-foreground',
    success: 'bg-success text-success-foreground',
    warning: 'bg-warning text-warning-foreground',
    destructive: 'bg-destructive text-destructive-foreground',
  },
  flat: {
    brand: 'bg-brand-accent text-brand',
    orange: 'bg-orange-accent text-orange',
    muted: 'bg-muted text-muted-foreground',
    success: 'bg-success-accent text-success',
    warning: 'bg-warning-accent text-warning',
    destructive: 'bg-destructive-accent text-destructive',
  },
};

const ChipVariants = cva(
  'font-regular absolute z-10 box-border flex origin-center scale-100 flex-wrap place-content-center items-center rounded-md whitespace-nowrap text-inherit subpixel-antialiased opacity-100 select-none data-[invisible=true]:scale-0 data-[invisible=true]:opacity-0',
  {
    variants: {
      variant: {
        solid: '',
        flat: '',
      },
      variantColor: {
        brand: '',
        orange: '',
        muted: '',
        success: '',
        warning: '',
        destructive: '',
      },
      size: {
        sm: 'text-xs',
        md: 'text-xs',
        lg: 'text-sm',
      },
      isInvisible: {
        true: '',
      },
      isOneChar: {
        true: '',
      },
      isDot: {
        true: 'p-0',
        false: 'px-0.5 py-px',
      },
      placement: {
        'top-right': '',
        'top-left': '',
        'bottom-right': '',
        'bottom-left': '',
      },
    },
    defaultVariants: {
      variant: 'solid',
      variantColor: 'brand',
      size: 'md',
      placement: 'top-right',
      isInvisible: false,
      isDot: false,
    },
    compoundVariants: [
      // solid / color
      {
        variant: 'solid',
        variantColor: 'muted',
        className: colorVariants.solid.muted,
      },
      {
        variant: 'solid',
        variantColor: 'brand',
        className: colorVariants.solid.brand,
      },
      {
        variant: 'solid',
        variantColor: 'orange',
        className: colorVariants.solid.orange,
      },
      {
        variant: 'solid',
        variantColor: 'success',
        className: colorVariants.solid.success,
      },
      {
        variant: 'solid',
        variantColor: 'warning',
        className: colorVariants.solid.warning,
      },
      {
        variant: 'solid',
        variantColor: 'destructive',
        className: colorVariants.solid.destructive,
      },
      // flat / color
      {
        variant: 'flat',
        variantColor: 'muted',
        className: colorVariants.flat.muted,
      },
      {
        variant: 'flat',
        variantColor: 'brand',
        className: colorVariants.flat.brand,
      },
      {
        variant: 'flat',
        variantColor: 'orange',
        className: colorVariants.flat.orange,
      },
      {
        variant: 'flat',
        variantColor: 'success',
        className: colorVariants.flat.success,
      },
      {
        variant: 'flat',
        variantColor: 'warning',
        className: colorVariants.flat.warning,
      },
      {
        variant: 'flat',
        variantColor: 'destructive',
        className: colorVariants.flat.destructive,
      },

      // isOneChar / size
      {
        isOneChar: true,
        size: 'sm',
        className: 'h-3 min-h-3 w-3 min-w-3',
      },
      {
        isOneChar: true,
        size: 'md',
        className: 'h-4 min-h-4 w-4 min-w-4',
      },
      {
        isOneChar: true,
        size: 'lg',
        className: 'h-5 min-h-5 w-5 min-w-5',
      },
      // isDot / size
      {
        isDot: true,
        size: 'sm',
        className: 'h-1.5 min-h-1.5 w-1.5 min-w-1.5',
      },
      {
        isDot: true,
        size: 'md',
        className: 'h-2 min-h-2 w-2 min-w-2',
      },
      {
        isDot: true,
        size: 'lg',
        className: 'h-2.5 min-h-2.5 w-2.5 min-w-2.5',
      },
      // placement
      {
        placement: 'top-right',
        className: 'top-1 right-1 translate-x-1/2 -translate-y-1/2',
      },
      {
        placement: 'top-left',
        className: 'top-1 left-1 -translate-x-1/2 -translate-y-1/2',
      },
      {
        placement: 'bottom-right',
        className: 'right-1 bottom-1 translate-x-1/2 translate-y-1/2',
      },
      {
        placement: 'bottom-left',
        className: 'bottom-1 left-1 -translate-x-1/2 translate-y-1/2',
      },
    ],
  }
);

export interface ChipProps extends VariantProps<typeof ChipVariants> {
  ref?: RefObject<HTMLSpanElement | null>;
  children: ReactNode;
  content?: string | number | ReactNode;
  className?: string;
}

export function Chip(props: ChipProps) {
  const { children, content, className, ...otherProps } = props;

  const { getChipProps } = useChip({ ...otherProps, content });

  return (
    <div className={cn('relative inline-flex h-fit w-fit shrink-0 overflow-visible', className)}>
      {children}
      <span {...getChipProps()}>{content}</span>
    </div>
  );
}

type useChipProps = Omit<ChipProps, 'children'>;
interface DOMElement extends Element, HTMLOrSVGElement {}

type DataAttributes = Record<string, unknown>;

type DOMAttributes<T = DOMElement> = React.AriaAttributes &
  React.DOMAttributes<T> &
  DataAttributes & {
    id?: string;
    role?: React.AriaRole;
    tabIndex?: number;
    style?: React.CSSProperties;
  };

type Merge<M, N> = N extends Record<string, unknown> ? M : Omit<M, keyof N> & N;

type PropGetter<P = Record<string, unknown>, R = DOMAttributes> = (
  props?: Merge<DOMAttributes, P>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: Ref<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => R & RefAttributes<any>;

function useChip(props: useChipProps) {
  const { content, className, isOneChar, isDot, variant, variantColor, isInvisible, size, placement, ...otherProps } = props;

  const isOneCharProp = useMemo(() => String(content).length === 1 || isOneChar, [content, isOneChar]);

  const isDotProp = useMemo(() => String(content).length === 0 || isDot, [content, isDot]);

  const getChipProps: PropGetter = () => {
    return {
      className: cn(
        ChipVariants({
          variant: variant,
          variantColor: variantColor,
          size: size,
          placement: placement,
          isOneChar: isOneCharProp,
          isDot: isDotProp,
        }),
        className
      ),
      'data-invisible': isInvisible,
      ...otherProps,
    };
  };

  return {
    isInvisible: isInvisible,
    getChipProps,
  };
}
